import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// AirNav Radar topographic tiles — the same basemap the admin app uses
// (frontend_admin/src/Common/tileProvider.js).
const AIRNAV_TOPO_URL =
  'https://tiles.airnavradar.com/tiles/radarbox_topography_webp/webmercator/{z}/{x}/{y}.webp';

// Keep in sync with STATUS_COLOR in App.js (the radar uses the same palette).
const STATUS_COLOR = { CRITICAL: '#ff2222', WARNING: '#ff8c00', INFO: '#0099cc' };

// Ground length of each dashed heading/track line, in km.
const TRACK_LINE_KM = 20;

// Great-circle destination point from (lat,lng) along a compass bearing.
// Used to draw each aircraft's dashed track line in its heading direction,
// mirroring the radar's "track line to the compass edge".
function destPoint(lat, lng, bearingDeg, distKm) {
  const R = 6371;
  const br = (bearingDeg * Math.PI) / 180;
  const lat1 = (lat * Math.PI) / 180;
  const lng1 = (lng * Math.PI) / 180;
  const dr = distKm / R;
  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(dr) + Math.cos(lat1) * Math.sin(dr) * Math.cos(br),
  );
  const lng2 = lng1 + Math.atan2(
    Math.sin(br) * Math.sin(dr) * Math.cos(lat1),
    Math.cos(dr) - Math.sin(lat1) * Math.sin(lat2),
  );
  return [(lat2 * 180) / Math.PI, (lng2 * 180) / Math.PI];
}

// Build the same arrow-triangle marker the radar draws (App.js "Aircraft"),
// rotated to the geographic track, with the distance / alt-diff / vertical-speed
// labels stacked next to it. The map is north-up, so the arrow rotates straight
// by track_deg (no track-up offset). Anchored on the aircraft coordinate.
function aircraftDivIcon(ac, C) {
  const col = STATUS_COLOR[ac.status] || C.faint;
  const bold = ac.status === 'CRITICAL' || ac.status === 'WARNING';
  const isAlert = STATUS_COLOR[ac.status] != null;
  const sz = bold ? 9 : 6;
  const ax = 18, ay = 24; // arrow (and icon anchor) position within the SVG

  const altSign = ac.alt_diff_m != null ? (ac.alt_diff_m > 0 ? '▲' : '▼') : '';
  const altAbs = ac.alt_diff_m != null ? `${Math.abs(Math.round(ac.alt_diff_m))}m` : '';
  const vs = ac.vertical_speed;
  const vsSign = vs != null && Math.round(vs) !== 0 ? (vs > 0 ? '↑' : '↓') : '';
  const vsLabel = vs != null ? `${vsSign}${Math.abs(Math.round(vs))} m/s` : '';
  const lines = [ac.distance_km_display || '', `${altSign}${altAbs}`, vsLabel].filter(Boolean);
  const firstY = ay - (lines.length - 1) * 6 + 4; // vertically centre the block on the arrow

  const ring = isAlert
    ? `<circle cx="${ax}" cy="${ay}" r="${sz + 6}" fill="none" stroke="${col}" stroke-width="1.5" opacity="0.3" />`
    : '';
  const arrow =
    `<polygon points="0,${-sz * 1.4} ${-sz * 0.7},${sz * 0.8} ${sz * 0.7},${sz * 0.8}" ` +
    `fill="${col}" stroke="#111" stroke-width="1.2" stroke-linejoin="round" ` +
    `transform="translate(${ax},${ay}) rotate(${ac.track_deg || 0})" />`;
  const texts = lines.map((t, i) =>
    `<text x="34" y="${firstY + i * 12}" fill="${col}" stroke="#fff" stroke-width="2.5" ` +
    `paint-order="stroke" stroke-linejoin="round" font-size="11" font-weight="700" ` +
    `font-family="monospace">${t}</text>`).join('');

  return L.divIcon({
    html: `<svg width="150" height="48" viewBox="0 0 150 48" style="overflow:visible">${ring}${arrow}${texts}</svg>`,
    className: 'radar-ac-icon',
    iconSize: [150, 48],
    iconAnchor: [ax, ay],
  });
}

// Pilot marker: same triangle as the radar's own-ship, in the pilot color,
// rotated to the pilot's heading.
const PILOT_AX = 12, PILOT_AY = 12; // arrow (and icon anchor) position within the SVG
const pilotRotation = hdg => `translate(${PILOT_AX},${PILOT_AY}) rotate(${hdg || 0})`;

function pilotDivIcon(hdg, C) {
  const sz = 9;
  const pts = `0,${-sz * 1.4} ${-sz * 0.8},${sz * 0.8} ${sz * 0.8},${sz * 0.8}`;
  return L.divIcon({
    html: `<svg width="24" height="24" viewBox="0 0 24 24" style="overflow:visible">` +
      `<polygon points="${pts}" fill="${C.pilot}" stroke="${C.pilotOutline}" stroke-width="1.5" ` +
      `stroke-linejoin="round" transform="${pilotRotation(hdg)}" /></svg>`,
    className: 'radar-ac-icon',
    iconSize: [24, 24],
    iconAnchor: [PILOT_AX, PILOT_AY],
  });
}

function collectPoints(pilot, aircraft) {
  const pts = [];
  if (pilot && pilot.lat != null && pilot.lng != null) pts.push([pilot.lat, pilot.lng]);
  (aircraft || []).forEach((ac) => {
    if (ac.lat != null && ac.lng != null) pts.push([ac.lat, ac.lng]);
  });
  return pts;
}

// Leaflet map that replaces the SVG radar circle in "MAP" mode. Rendered
// imperatively (no react-leaflet) so it drops cleanly into this React 19 app
// with no extra peer deps. Pilot + aircraft come straight from the scene
// `calculations` (each carries lat/lng/track/alt from the guard).
export default function RadarMap({ pilot, aircraft, heading, C }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef(null);
  const pilotMarkerRef = useRef(null);
  const pilotLineRef = useRef(null);
  // Latest data, so the mount effect can frame the initial view without
  // depending on props (keeps its deps empty → one map per mount).
  const dataRef = useRef({ pilot, aircraft });
  dataRef.current = { pilot, aircraft };
  // Own-ship heading: the live device compass when the radar has one, else the
  // pilot track the backend last stored. Held in a ref so the marker-rebuild
  // effect can read it without listing it as a dependency — deviceorientation
  // fires many times a second and rebuilding every marker at that rate would
  // thrash the map. The rotation-only effect below handles the updates.
  const headingRef = useRef(null);
  headingRef.current = heading != null ? heading : (pilot?.hdg ?? null);

  // Create the map once per mount, and frame pilot + traffic. The initial fit
  // lives here (not in a shared ref latch) so it runs per map instance and
  // survives React StrictMode's mount/unmount/mount in dev.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const { pilot: p, aircraft: acs } = dataRef.current;
    const pts = collectPoints(p, acs);
    const map = L.map(containerRef.current, {
      center: pts.length ? pts[0] : [46.5, 14.0],
      zoom: 12,
      zoomControl: true,
      attributionControl: false,
    });
    L.tileLayer(AIRNAV_TOPO_URL, { maxZoom: 18 }).addTo(map);
    markersRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;

    // Recompute size + frame the view. Runs a few times right after mount
    // because the container can still be settling its layout (e.g. the card
    // resizing), which would otherwise leave Leaflet with a stale size and a
    // half-tiled, mis-centred map.
    const frameView = () => {
      map.invalidateSize();
      if (pts.length > 1) {
        map.fitBounds(L.latLngBounds(pts), { padding: [50, 50], maxZoom: 13 });
      } else if (pts.length === 1) {
        map.setView(pts[0], 12);
      }
    };
    frameView();
    const t1 = setTimeout(frameView, 60);
    const t2 = setTimeout(frameView, 250);

    // Keep the map sized to its container afterwards (orientation / window
    // changes) without re-centering, so the user's pan/zoom is preserved.
    const ro = new ResizeObserver(() => map.invalidateSize());
    ro.observe(containerRef.current);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      ro.disconnect();
      map.remove();
      mapRef.current = null;
      markersRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Redraw track lines + markers whenever the scene data changes.
  useEffect(() => {
    const group = markersRef.current;
    if (!group) return;
    group.clearLayers();

    // Dashed track lines go on first so the arrow markers (in the marker pane)
    // sit above them.
    pilotLineRef.current = null;
    pilotMarkerRef.current = null;
    if (pilot && pilot.lat != null && pilot.lng != null && headingRef.current != null) {
      const end = destPoint(pilot.lat, pilot.lng, headingRef.current, TRACK_LINE_KM);
      pilotLineRef.current = L.polyline([[pilot.lat, pilot.lng], end], {
        color: C.pilot, weight: 1.5, opacity: 0.55, dashArray: '4 5',
      }).addTo(group);
    }
    (aircraft || []).forEach((ac) => {
      if (ac.lat == null || ac.lng == null || ac.track_deg == null) return;
      const bold = ac.status === 'CRITICAL' || ac.status === 'WARNING';
      const end = destPoint(ac.lat, ac.lng, ac.track_deg, TRACK_LINE_KM);
      L.polyline([[ac.lat, ac.lng], end], {
        color: STATUS_COLOR[ac.status] || C.faint,
        weight: bold ? 2 : 1.4, opacity: 0.55, dashArray: '4 5',
      }).addTo(group);
    });

    // Markers on top.
    if (pilot && pilot.lat != null && pilot.lng != null) {
      pilotMarkerRef.current = L
        .marker([pilot.lat, pilot.lng], { icon: pilotDivIcon(headingRef.current, C), interactive: false })
        .addTo(group);
    }
    (aircraft || []).forEach((ac) => {
      if (ac.lat == null || ac.lng == null) return;
      L.marker([ac.lat, ac.lng], { icon: aircraftDivIcon(ac, C), interactive: false })
        .addTo(group);
    });
  }, [pilot, aircraft, C]);

  // Swing the own-ship arrow (and its track line) to the current heading. The
  // map itself stays north-up, so unlike the radar's track-up view it's the
  // icon that turns. Poking the existing SVG/polyline directly keeps this cheap
  // enough to run on every compass reading.
  useEffect(() => {
    const hdg = headingRef.current;
    if (hdg == null) return;

    const el = pilotMarkerRef.current?.getElement();
    const arrow = el && el.querySelector('polygon');
    if (arrow) arrow.setAttribute('transform', pilotRotation(hdg));

    if (pilotLineRef.current && pilot && pilot.lat != null && pilot.lng != null) {
      pilotLineRef.current.setLatLngs([
        [pilot.lat, pilot.lng],
        destPoint(pilot.lat, pilot.lng, hdg, TRACK_LINE_KM),
      ]);
    }
  }, [heading, pilot]);

  return <div ref={containerRef} className="radar-map" style={{ width: '100%', height: '100%' }} />;
}
